import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { apiClient } from "@/lib/api-client";
import { useToast } from "@/hooks/use-toast";
import { getApiErrorMessage } from "@/lib/errors";
import type {
    CreateOrganizationRequest,
    UpdateOrganizationRequest,
    OrganizationLinkedContact,
    OrganizationLinkedProperty,
} from "@/api/generated/client/models";

// ===============================
// Organization CRUD
// ===============================

export const ORGANIZATIONS_PAGE_SIZE = 50;

export interface OrganizationsFilters {
    orgType?: string;
    search?: string;
    page?: number;
    limit?: number;
}

export function useOrganizations(filters: OrganizationsFilters = {}) {
    const { orgType, search, page = 1, limit = ORGANIZATIONS_PAGE_SIZE } = filters;
    const queryClient = useQueryClient();
    const { toast } = useToast();

    const offset = (page - 1) * limit;

    const { data: response, isLoading, isPlaceholderData } = useQuery({
        queryKey: ["organizations", orgType, search, page, limit],
        queryFn: async () => {
            return await apiClient.organizations.listOrganizations({
                orgType,
                search: search || undefined,
                limit,
                offset,
            });
        },
        placeholderData: keepPreviousData,
    });

    const organizations = response?.organizations || [];
    const total = response?.meta?.total ?? organizations.length;
    const totalPages = Math.ceil(total / limit);

    const createMutation = useMutation({
        mutationFn: async (data: CreateOrganizationRequest) => {
            return await apiClient.organizations.createOrganization({
                createOrganizationRequest: data,
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["organizations"] });
            toast({ title: "Organization created", description: "The organization has been added." });
        },
        onError: async (error: unknown) => {
            const message = await getApiErrorMessage(error);
            toast({ title: "Failed to create organization", description: message, variant: "destructive" });
        },
    });

    const updateMutation = useMutation({
        mutationFn: async ({ id, data }: { id: number; data: UpdateOrganizationRequest }) => {
            return await apiClient.organizations.updateOrganization({
                id,
                updateOrganizationRequest: data,
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["organizations"] });
            toast({ title: "Organization updated" });
        },
        onError: async (error: unknown) => {
            const message = await getApiErrorMessage(error);
            toast({ title: "Failed to update organization", description: message, variant: "destructive" });
        },
    });

    const deleteMutation = useMutation({
        mutationFn: async (id: number) => {
            return await apiClient.organizations.deleteOrganization({ id });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["organizations"] });
            toast({ title: "Organization deleted" });
        },
        onError: async (error: unknown) => {
            const message = await getApiErrorMessage(error);
            toast({ title: "Failed to delete organization", description: message, variant: "destructive" });
        },
    });

    return {
        organizations,
        total,
        totalPages,
        isLoading,
        isPlaceholderData,
        createOrganization: createMutation.mutateAsync,
        updateOrganization: updateMutation.mutateAsync,
        deleteOrganization: deleteMutation.mutate,
        isCreating: createMutation.isPending,
        isUpdating: updateMutation.isPending,
        isDeleting: deleteMutation.isPending,
    };
}

// ===============================
// Organization ↔ Contact
// ===============================

export function useOrganizationContacts(organizationId: number | null) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const { data, isLoading } = useQuery({
    queryKey: ["organization-contacts", organizationId],
    queryFn: async () => {
      if (!organizationId) return { contacts: [] as OrganizationLinkedContact[] };
      const res = await apiClient.organizations.getOrganizationContacts({ id: organizationId });
      return { contacts: res.contacts ?? [] };
    },
    enabled: !!organizationId,
  });

  const linkMutation = useMutation({
    mutationFn: async (body: { contactId: number; role?: string; isPrimary?: boolean; notes?: string }) => {
      if (organizationId == null) throw new Error("Cannot link contact: organizationId is not set");
      await apiClient.organizations.linkOrganizationContact({
        id: organizationId,
        linkOrganizationContactRequest: body,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["organization-contacts", organizationId] });
      toast({ title: "Contact linked" });
    },
    onError: async (error: unknown) => {
      const message = await getApiErrorMessage(error);
      toast({ title: "Failed to link contact", description: message, variant: "destructive" });
    },
  });

  const unlinkMutation = useMutation({
    mutationFn: async (contactId: number) => {
      if (organizationId == null) throw new Error("Cannot unlink contact: organizationId is not set");
      await apiClient.organizations.unlinkOrganizationContact({
        id: organizationId,
        contactId,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["organization-contacts", organizationId] });
      toast({ title: "Contact unlinked" });
    },
    onError: async (error: unknown) => {
      const message = await getApiErrorMessage(error);
      toast({ title: "Failed to unlink contact", description: message, variant: "destructive" });
    },
  });

  return {
    contacts: data?.contacts ?? [],
    isLoading,
    linkContact: linkMutation.mutateAsync,
    unlinkContact: unlinkMutation.mutate,
    isLinking: linkMutation.isPending,
  };
}

// ===============================
// Organization ↔ Property
// ===============================

export function useOrganizationProperties(organizationId: number | null) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const queryKey = ["organization-properties", organizationId];

  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: async () => {
      if (!organizationId) return { properties: [] as OrganizationLinkedProperty[] };
      const res = await apiClient.organizations.getOrganizationProperties({ id: organizationId });
      return { properties: res.properties ?? [] };
    },
    enabled: !!organizationId,
  });

  const linkMutation = useMutation({
    mutationFn: async (propertyId: number) => {
      if (organizationId == null) throw new Error("Cannot link property: organizationId is not set");
      await apiClient.organizations.linkOrganizationProperty({
        id: organizationId,
        linkOrganizationPropertyRequest: { propertyId },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      toast({ title: "Property linked" });
    },
    onError: async (error: unknown) => {
      const message = await getApiErrorMessage(error);
      toast({ title: "Failed to link property", description: message, variant: "destructive" });
    },
  });

  const unlinkMutation = useMutation({
    mutationFn: async (propertyId: number) => {
      if (organizationId == null) throw new Error("Cannot unlink property: organizationId is not set");
      await apiClient.organizations.unlinkOrganizationProperty({
        id: organizationId,
        propertyId,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      toast({ title: "Property unlinked" });
    },
    onError: async (error: unknown) => {
      const message = await getApiErrorMessage(error);
      toast({ title: "Failed to unlink property", description: message, variant: "destructive" });
    },
  });

  return {
    properties: data?.properties ?? [],
    isLoading,
    linkProperty: linkMutation.mutateAsync,
    unlinkProperty: unlinkMutation.mutate,
    isLinking: linkMutation.isPending,
  };
}

// ===============================
// Organization ↔ Claims (reverse lookup)
// ===============================

export interface OrganizationClaimLink {
  id: number;
  companyId: number;
  claimId: number;
  organizationId: number;
  role: string;
  notes: string | null;
  insuredName?: string;
  claimNumber?: string;
  createdAt: string;
}

export function useOrganizationClaims(organizationId: number | null, orgType?: string) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const { data, isLoading } = useQuery({
    queryKey: ["organization-claims", organizationId],
    queryFn: async () => {
      if (!organizationId) return { claimOrganizations: [] as OrganizationClaimLink[] };
      const res = await apiClient.organizations.getOrganizationClaims({ id: organizationId });
      return { claimOrganizations: res.claimOrganizations ?? [] };
    },
    enabled: !!organizationId,
  });

  const linkMutation = useMutation({
    mutationFn: async (claimId: number) => {
      if (organizationId == null) throw new Error("Cannot link claim: organizationId is not set");
      await apiClient.claims.linkClaimOrganization({
        claimId,
        linkClaimOrganizationRequest: { organizationId, role: orgType || "Other" },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["organization-claims", organizationId] });
      toast({ title: "Claim linked" });
    },
    onError: async (error: unknown) => {
      const message = await getApiErrorMessage(error);
      toast({ title: "Failed to link claim", description: message, variant: "destructive" });
    },
  });

  const unlinkMutation = useMutation({
    mutationFn: async (claimId: number) => {
      if (organizationId == null) throw new Error("Cannot unlink claim: organizationId is not set");
      await apiClient.claims.unlinkClaimOrganization({ claimId, organizationId });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["organization-claims", organizationId] });
      toast({ title: "Claim unlinked" });
    },
    onError: async (error: unknown) => {
      const message = await getApiErrorMessage(error);
      toast({ title: "Failed to unlink claim", description: message, variant: "destructive" });
    },
  });

  return {
    organizationClaims: data?.claimOrganizations || [],
    isLoading,
    linkClaim: linkMutation.mutateAsync,
    unlinkClaim: unlinkMutation.mutate,
  };
}
